import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Grid,
  TextField,
  IconButton,
  Card,
  CardContent,
  Chip,
  Divider,
} from '@mui/material';
import { ArrowBack, Add, Delete, Person } from '@mui/icons-material';
import LandingLayout from './LandingLayout';
import TimezoneSelector from './TimezoneSelector';
import ProgressBar from './ProgressBar';

interface Caregiver {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  timezone: string;
  alertOrder: number;
}

const CareCircleSetup: React.FC = () => {
  const [caregivers, setCaregivers] = useState<Caregiver[]>([
    {
      id: 1,
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      timezone: 'America/Los_Angeles',
      alertOrder: 1,
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  // Prepopulate the first caregiver with user's account information
  useEffect(() => {
    const userInfo = localStorage.getItem('signupUserInfo');
    if (userInfo) {
      try {
        const { firstName, lastName, phoneNumber, timezone, email } = JSON.parse(userInfo);
        setCaregivers(prev => prev.map((caregiver, index) => 
          index === 0 ? {
            ...caregiver,
            firstName: firstName || '',
            lastName: lastName || '',
            phoneNumber: phoneNumber || '',
            email: email || '',
            timezone: timezone || 'America/Los_Angeles'
          } : caregiver
        ));
      } catch (e) {
        console.log('No user info found in localStorage');
      }
    }

    // Load saved care circle data if available
    const savedCareCircleInfo = localStorage.getItem('signupCareCircleInfo');
    if (savedCareCircleInfo) {
      try {
        const careCircleInfo = JSON.parse(savedCareCircleInfo);
        if (careCircleInfo.caregivers && careCircleInfo.caregivers.length > 0) {
          const loadedCaregivers = careCircleInfo.caregivers.map((cg: any, index: number) => ({
            id: index + 1,
            firstName: cg.firstName || '',
            lastName: cg.lastName || '',
            phoneNumber: cg.phoneNumber || '',
            email: cg.email || '',
            timezone: 'America/Los_Angeles',
            alertOrder: cg.alertOrder || index + 1,
          }));
          setCaregivers(loadedCaregivers);
        }
      } catch (e) {
        console.error('Error loading saved care circle info:', e);
      }
    }
  }, []);

  const addCaregiver = () => {
    if (caregivers.length >= 4) return;
    
    const newCaregiver: Caregiver = {
      id: Date.now(),
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      timezone: 'America/Los_Angeles',
      alertOrder: caregivers.length + 1,
    };
    
    setCaregivers([...caregivers, newCaregiver]);
  };

  const removeCaregiver = (id: number) => {
    if (caregivers.length <= 1) return;
    
    const updatedCaregivers = caregivers
      .filter(c => c.id !== id)
      .map((c, index) => ({ ...c, alertOrder: index + 1 }));
    
    setCaregivers(updatedCaregivers);
  };

  const updateCaregiver = (id: number, field: keyof Caregiver, value: string | number) => {
    setCaregivers(caregivers.map(c => 
      c.id === id ? { ...c, [field]: value } : c
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validation
    for (let i = 0; i < caregivers.length; i++) {
      const caregiver = caregivers[i];
      if (!caregiver.firstName.trim()) {
        setError('First name is required for all caregivers');
        setLoading(false);
        return;
      }

      if (!caregiver.lastName.trim()) {
        setError('Last name is required for all caregivers');
        setLoading(false);
        return;
      }

      if (!caregiver.phoneNumber.trim()) {
        setError('Phone number is required for all caregivers');
        setLoading(false);
        return;
      }

      // Basic phone number validation
      const phoneRegex = /^\+?1?\s*\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;
      if (!phoneRegex.test(caregiver.phoneNumber.trim())) {
        setError('Please enter valid phone numbers for all caregivers (e.g., +1 (555) 123-4567)');
        setLoading(false);
        return;
      }

      // Email is required for additional caregivers (not the first one)
      if (i > 0 && !caregiver.email.trim()) {
        setError('Email is required for additional caregivers');
        setLoading(false);
        return;
      }

      // Email format validation for additional caregivers
      if (i > 0 && caregiver.email.trim()) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(caregiver.email.trim())) {
          setError('Please enter a valid email address for additional caregivers');
          setLoading(false);
          return;
        }
      }
    }

    try {
      // Save care circle information to localStorage for the next step
      // No database write until payment completion
      const customerInfo = JSON.parse(localStorage.getItem('signupCustomerInfo') || '{}');
      
      const careCircleInfo = {
        caregivers: caregivers.map(caregiver => ({
          firstName: caregiver.firstName.trim(),
          lastName: caregiver.lastName.trim(),
          phoneNumber: caregiver.phoneNumber.trim(),
          email: caregiver.email || '',
          alertOrder: caregiver.alertOrder,
          role: caregiver.alertOrder === 1 ? 'PRIMARY' : 'SECONDARY',
          isPrimary: caregiver.alertOrder === 1
        }))
      };
      
      localStorage.setItem('signupCareCircleInfo', JSON.stringify(careCircleInfo));
      
      // Navigate to payment page
      navigate('/signup/payment');
    } catch (err) {
      setError('Failed to save care circle information. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Save current form data before navigating back
    const careCircleInfo = {
      caregivers: caregivers.map(caregiver => ({
        firstName: caregiver.firstName.trim(),
        lastName: caregiver.lastName.trim(),
        phoneNumber: caregiver.phoneNumber.trim(),
        email: caregiver.email || '',
        alertOrder: caregiver.alertOrder,
        role: caregiver.alertOrder === 1 ? 'PRIMARY' : 'SECONDARY',
        isPrimary: caregiver.alertOrder === 1
      }))
    };
    localStorage.setItem('signupCareCircleInfo', JSON.stringify(careCircleInfo));
    navigate('/signup/loved-one');
  };

  return (
    <LandingLayout>
      <Container component="main" maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 800, mb: 4 }}>
            <ProgressBar
              currentStep={4}
              totalSteps={5}
              stepLabels={['Plan', 'Account', 'Loved One', 'Care Circle', 'Payment']}
            />
          </Box>
          
          <Paper elevation={3} sx={{ p: 6, width: '100%', maxWidth: 900 }}>
            <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
              Set Up Your Care Circle
            </Typography>
            
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Add up to 4 caregivers who will receive alerts if your loved one doesn't respond to check-ins
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                How Escalation Works
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                If your loved one doesn't respond to a check-in, we'll alert caregivers in order:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="1st" size="small" color="primary" />
                  <Typography variant="body2">First caregiver receives immediate alert</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="2nd" size="small" color="secondary" />
                  <Typography variant="body2">Second caregiver alerted if first doesn't respond within 15 minutes</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="3rd" size="small" color="default" />
                  <Typography variant="body2">Third caregiver alerted if second doesn't respond within 30 minutes</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip label="4th" size="small" color="default" />
                  <Typography variant="body2">Fourth caregiver alerted if third doesn't respond within 1 hour</Typography>
                </Box>
              </Box>
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              {caregivers.map((caregiver, index) => (
                <Card key={caregiver.id} sx={{ mb: 3, border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person sx={{ color: '#2E7D32' }} />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          Caregiver {caregiver.alertOrder}
                        </Typography>
                        {index === 0 && (
                          <Chip label="You" size="small" color="primary" />
                        )}
                      </Box>
                      {caregivers.length > 1 && (
                        <IconButton
                          onClick={() => removeCaregiver(caregiver.id)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          name="caregiverFirstName"
                          label="First Name"
                          value={caregiver.firstName}
                          onChange={(e) => updateCaregiver(caregiver.id, 'firstName', e.target.value)}
                          disabled={loading}
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          name="caregiverLastName"
                          label="Last Name"
                          value={caregiver.lastName}
                          onChange={(e) => updateCaregiver(caregiver.id, 'lastName', e.target.value)}
                          disabled={loading}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          required
                          fullWidth
                          name="caregiverPhoneNumber"
                          label="Phone Number"
                          type="tel"
                          value={caregiver.phoneNumber}
                          onChange={(e) => updateCaregiver(caregiver.id, 'phoneNumber', e.target.value)}
                          disabled={loading}
                          placeholder="+1 (555) 123-4567"
                          helperText="This is where we'll send alert notifications"
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <TextField
                          required={index > 0}
                          fullWidth
                          name="caregiverEmail"
                          label={index === 0 ? "Email (Optional)" : "Email"}
                          type="email"
                          value={caregiver.email}
                          onChange={(e) => updateCaregiver(caregiver.id, 'email', e.target.value)}
                          disabled={loading}
                          placeholder="caregiver@example.com"
                          helperText={index === 0 ? "Optional: for email notifications" : "Required for additional caregivers"}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                          Timezone
                        </Typography>
                        <TimezoneSelector
                          onTimezoneChange={(newTimezone) => updateCaregiver(caregiver.id, 'timezone', newTimezone)}
                          showCurrentTime={false}
                          allowSave={false}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))}

              {caregivers.length < 4 && (
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={addCaregiver}
                    disabled={loading}
                    sx={{
                      borderColor: '#2E7D32',
                      color: '#2E7D32',
                      '&:hover': {
                        borderColor: '#1B5E20',
                        backgroundColor: 'rgba(46, 125, 50, 0.04)',
                      },
                    }}
                  >
                    Add Another Caregiver
                  </Button>
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handleBack}
                  disabled={loading}
                  sx={{
                    borderColor: '#2E7D32',
                    color: '#2E7D32',
                    '&:hover': {
                      borderColor: '#1B5E20',
                      backgroundColor: 'rgba(46, 125, 50, 0.04)',
                    },
                  }}
                >
                  Back
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    px: 4,
                    backgroundColor: '#2E7D32',
                    '&:hover': {
                      backgroundColor: '#1B5E20'
                    },
                    fontSize: '1.1rem',
                  }}
                >
                  {loading ? 'Saving...' : 'Next'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </LandingLayout>
  );
};

export default CareCircleSetup;
